import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';

import { ResourceTeam, ResourceTeamMember, ResourceUserOption } from '../../../../Models/resource-modules.models';
import { ResourceKnowledge } from '../../../../Models/resource.models';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { ResourceTeamManagementStore } from '../resource-team-management.store';

/**
 * Detailansicht eines Teams: Übersicht, Mitglieder (mit Teamleitung, Hauptteam und
 * Skills) sowie Mitglieder hinzufügen/entfernen und Teamleitung übertragen. Alle
 * Änderungen laufen über den gemeinsamen Store.
 */
@Component({
  selector: 'app-resource-team-detail',
  templateUrl: './resource-team-detail.component.html',
  styleUrls: ['../resource-team-management.shared.css', './resource-team-detail.component.css']
})
export class ResourceTeamDetailComponent implements OnChanges {
  @Input() team!: ResourceTeam;
  @Output() openProfile = new EventEmitter<string>();
  @Output() edit = new EventEmitter<ResourceTeam>();
  @Output() deleted = new EventEmitter<void>();

  newMemberIds: string[] = [];
  memberSearch = '';
  leaderId = '';
  memberFilter = '';

  constructor(
    public store: ResourceTeamManagementStore,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['team']) {
      const previous = changes['team'].previousValue as ResourceTeam | undefined;
      if (previous?.id !== this.team.id) {
        this.newMemberIds = [];
        this.memberSearch = '';
        this.memberFilter = '';
      }
      this.leaderId = this.team.leaderUserId ?? '';
    }
  }

  get canManage(): boolean { return this.store.canManageTeam(this.team); }

  get members(): ResourceTeamMember[] {
    const q = this.memberFilter.trim().toLocaleLowerCase('de');
    return this.team.members.filter(m => !q ||
      m.name.toLocaleLowerCase('de').includes(q) ||
      (m.email ?? '').toLocaleLowerCase('de').includes(q) ||
      this.memberSkills(m.userId).some(s => s.name.toLocaleLowerCase('de').includes(q)));
  }

  get availableUsers(): ResourceUserOption[] {
    const memberIds = new Set(this.team.members.map(m => m.userId));
    const q = this.memberSearch.trim().toLocaleLowerCase('de');
    return this.store.userOptions.filter(u => !memberIds.has(u.userId) && (
      this.newMemberIds.includes(u.userId) || !q ||
      u.name.toLocaleLowerCase('de').includes(q) ||
      (u.email ?? '').toLocaleLowerCase('de').includes(q)));
  }

  memberSkills(userId: string): ResourceKnowledge[] {
    const profile = this.store.profile(userId);
    return profile ? this.store.skills.filter(s => profile.skillIds.includes(s.id)) : [];
  }

  /** Ist dieses Team das Hauptteam des Mitglieds? */
  isPrimary(member: ResourceTeamMember): boolean {
    const profile = this.store.profile(member.userId);
    return profile ? profile.primaryTeamId === this.team.id : member.isPrimaryTeam;
  }

  teamCount(userId: string): number {
    return this.store.profile(userId)?.memberships.length ?? 0;
  }

  canOpenProfile(userId: string): boolean {
    return !!this.store.profile(userId);
  }

  /** Eigene Hauptteam-Auswahl, wenn der Benutzer mehreren aktiven Teams angehört. */
  get canMakeMyPrimary(): boolean {
    const me = this.team.members.find(m => m.userId === this.store.currentUserId);
    const myTeams = (this.store.access?.teams ?? []).length;
    return !!me && this.team.isActive && myTeams > 1 && !(this.store.access?.teams ?? [])
      .some(t => t.teamId === this.team.id && t.isPrimaryTeam);
  }

  trackMember(_i: number, member: ResourceTeamMember): string { return member.userId; }

  // ---- Aktionen -------------------------------------------------------------------

  addMembers(): void {
    if (!this.canManage || !this.newMemberIds.length || this.store.busy) return;
    const count = this.newMemberIds.length;
    this.handle(this.store.addMembers(this.team.id, this.newMemberIds),
      count === 1 ? 'Mitglied hinzugefügt.' : `${count} Mitglieder hinzugefügt.`,
      () => { this.newMemberIds = []; this.memberSearch = ''; });
  }

  removeMember(member: ResourceTeamMember): void {
    if (!this.canManage || member.isLeader || this.store.busy) return;
    const remaining = this.teamCount(member.userId) - 1;
    this.confirm({
      title: 'Mitglied entfernen',
      message: `${member.name} aus dem Team „${this.team.name}“ entfernen?`,
      details: [
        'Bestehende Planungstermine bleiben erhalten.',
        ...(remaining === 1 ? ['Das verbleibende Team wird automatisch zum Hauptteam.'] : []),
        ...(remaining > 1 && this.isPrimary(member) ? ['Für mehrere verbleibende Teams muss danach ein neues Hauptteam gewählt werden.'] : [])
      ],
      confirmText: 'Entfernen'
    }, () => this.handle(this.store.removeMember(this.team.id, member.userId), 'Mitglied entfernt.'));
  }

  changeLeader(): void {
    if (!this.canManage || !this.leaderId || this.leaderId === this.team.leaderUserId || this.store.busy) return;
    const newLeader = this.team.members.find(m => m.userId === this.leaderId)?.name ?? '';
    const losesRights = !this.store.isGlobal && this.team.leaderUserId === this.store.currentUserId;
    this.confirm({
      title: this.team.leaderUserId ? 'Teamleitung übertragen' : 'Teamleitung festlegen',
      message: `${newLeader} wird Teamleitung von „${this.team.name}“.`,
      details: this.team.leaderName ? [`${this.team.leaderName} bleibt Mitglied des Teams.`] : undefined,
      warning: losesRights
        ? 'Danach kannst du dieses Team nicht mehr verwalten (Mitglieder, Planung, Skills und Kategorien).'
        : undefined,
      confirmText: 'Übernehmen'
    }, () => this.handle(this.store.changeLeader(this.team.id, this.leaderId), 'Teamleitung geändert.'));
  }

  makeMyPrimary(): void {
    if (this.store.busy) return;
    this.handle(this.store.setPrimaryTeam(this.team.id, this.store.currentUserId),
      `„${this.team.name}“ ist jetzt dein Hauptteam.`);
  }

  deleteTeam(): void {
    if (!this.canManage || this.store.busy) return;
    this.confirm({
      title: 'Team löschen',
      message: `Team „${this.team.name}“ endgültig löschen?`,
      details: [
        'Mitgliedschaften und Teamzuordnungen von Skills/Kategorien werden entfernt.',
        'Planungstermine und Urlaubsanträge bleiben erhalten.',
        'Wird das Team nur vorübergehend nicht gebraucht: stattdessen deaktivieren.'
      ],
      warning: 'Das Löschen kann nicht rückgängig gemacht werden.',
      confirmText: 'Endgültig löschen'
    }, () => this.handle(this.store.deleteTeam(this.team.id), `Team „${this.team.name}“ wurde gelöscht.`,
      () => this.deleted.emit()));
  }

  // ---- Hilfen ---------------------------------------------------------------------

  private handle(request: Observable<unknown>, message: string, done?: () => void): void {
    request.subscribe({
      next: () => { done?.(); this.snackBar.open(message, 'OK', { duration: 3000 }); },
      error: (err: HttpErrorResponse) => this.snackBar.open(
        ResourceTeamManagementStore.errorText(err, 'Die Aktion ist fehlgeschlagen.'), 'OK', { duration: 5000 })
    });
  }

  private confirm(data: ConfirmDialogData, onConfirm: () => void): void {
    this.dialog.open(ConfirmDialogComponent, { data, width: '460px', maxWidth: '95vw' })
      .afterClosed()
      .subscribe(ok => { if (ok) onConfirm(); });
  }
}
